import { useImpersonateMutation } from '@api/auth';
import { Building2, Settings2, UserCheck } from '@tamagui/lucide-icons';
import { Button } from '@unistocks/components/button/button';
import { Column, Row } from '@unistocks/components/layout/flex';
import { Page } from '@unistocks/components/page/page';
import { Heading } from '@unistocks/components/text/text';
import { toast } from '@unistocks/components/toast';
import { FeatureManagementSheet } from '@unistocks/features/admin/components/feature-management/feature-management-sheet';
import { tenantGetQueryKey, useTenantGetQuery } from '@unistocks/proxy/axios-client/TenantQuery';
import { useAppDispatch, useAppSelector } from '@unistocks/shared/hooks/redux-hooks';
import { usePermission } from '@unistocks/shared/hooks/use-permission';
import { t } from '@unistocks/shared/localization/index.ts';
import { UnistocksPermissions } from '@unistocks/shared/permissions';
import { AppActions, selectToken } from '@unistocks/shared/store/root.slice';
import React, { useState } from 'react';
import { ListItem, Separator, View } from 'tamagui';

type TenantDetailsProps = {
  id: string;
};

export const TenantDetails: React.FC<TenantDetailsProps> = ({ id }) => {
  const dispatch = useAppDispatch();
  const token = useAppSelector(selectToken);
  const hasFeatureManagement = usePermission(UnistocksPermissions.TenantManagementFeatures);
  const hasImpersonation = usePermission(UnistocksPermissions.TenantManagementImpersonation);
  const [featuresSheetOpen, setFeaturesSheetOpen] = useState(false);

  const { data: tenant, isSuccess } = useTenantGetQuery(id);

  const { mutate: impersonate, isPending: isImpersonating } = useImpersonateMutation({
    onSuccess: newToken => {
      dispatch(AppActions.startImpersonation(newToken));
    },
    onError: () => {
      toast.error(t('AbpUi::DefaultErrorMessage'));
    }
  });

  const handleImpersonate = () => {
    if (!token?.access_token) return;
    impersonate({ tenantId: id, accessToken: token.access_token });
  };

  return (
    <Page size="medium">
      <Page.ScrollView
        headerTitle={t('AbpTenantManagement::Tenant:Edit')}
        breadcrumbs={[
          {
            key: 'tenants',
            href: '/(admin)/tenant-management',
            name: t('AbpTenantManagement::Menu:TenantManagement'),
            rootPathName: 'tenants',
            Icon: props => <Building2 {...props} />
          }
        ]}
        queryState={{ queryKeys: [tenantGetQueryKey(id)] }}
      >
        {isSuccess ? (
          <Column gap={'$4'} mt={'$3'}>
            <Row gap={'$3'} alignItems="center">
              <Building2 size={'$4'} color={'$unistocks'} />
              <Heading size={'$8'}>{tenant.name}</Heading>
            </Row>

            <Page.Content>
              <ListItem title={t('AbpTenantManagement::TenantName')} subTitle={tenant.name} />
              <Separator />
              <ListItem title={'ID'} subTitle={tenant.id}></ListItem>

              <View gap={'$2'} $lg={{ fd: 'row' }}>
                {hasFeatureManagement && (
                  <Button
                    size={'$3'}
                    Icon={Settings2}
                    onPress={() => setFeaturesSheetOpen(true)}
                    $max-md={{
                      flex: 1
                    }}
                  >
                    {t('AbpFeatureManagement::ManageHostFeatures')}
                  </Button>
                )}

                {hasImpersonation && (
                  <Button
                    size={'$3'}
                    Icon={UserCheck}
                    onPress={handleImpersonate}
                    loading={isImpersonating}
                    $max-md={{
                      flex: 1
                    }}
                  >
                    {t('::Permission:TenantManagement:Impersonation')}
                  </Button>
                )}
              </View>
            </Page.Content>
          </Column>
        ) : null}
      </Page.ScrollView>

      <FeatureManagementSheet
        providerName="T"
        providerKey={id}
        open={featuresSheetOpen}
        onOpenChange={setFeaturesSheetOpen}
      />
    </Page>
  );
};

TenantDetails.displayName = 'TenantDetails';
